import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageSquare, CalendarDays, ArrowUpRight } from "lucide-react";
import { QUOTE_URL, CONTACT } from "../data/site";

export const MobileCtaBar = () => {
  const [show, setShow] = useState(false);
  const [callOpen, setCallOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 420);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="mobile-cta-bar"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
          className="fixed bottom-0 left-0 right-0 z-30 lg:hidden border-t border-[hsl(var(--gold)/0.28)] bg-[hsl(22_26%_10%/0.92)] backdrop-blur-xl shadow-[0_-8px_28px_rgba(0,0,0,0.35)]"
          style={{ paddingBottom: "max(0.55rem, env(safe-area-inset-bottom))" }}
        >
          <div className="flex items-center gap-2.5 px-4 pt-2.5">
            <div className="relative shrink-0 z-50">
              <button
                type="button"
                onClick={() => setCallOpen((v) => !v)}
                data-testid="mobile-cta-call"
                aria-label={`Call or text ${CONTACT.phone}`}
                aria-expanded={callOpen}
                className="relative z-50 grid place-items-center h-11 w-11 rounded-full border border-[hsl(var(--gold)/0.45)] text-[hsl(var(--gold-soft))] transition-colors duration-300 active:bg-[hsl(var(--gold)/0.18)]"
              >
                <Phone size={17} />
              </button>

              <AnimatePresence>
                {callOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setCallOpen(false)}
                      data-testid="mobile-cta-call-backdrop"
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                      data-testid="mobile-cta-call-menu"
                      className="absolute bottom-full left-0 z-50 mb-3 w-48 overflow-hidden rounded-2xl border border-[hsl(var(--gold)/0.3)] bg-[hsl(22_26%_12%)] shadow-[0_12px_34px_rgba(0,0,0,0.5)]"
                    >
                      <p className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-[0.18em] text-[hsl(30_20%_58%)]">
                        {CONTACT.phone}
                      </p>
                      <a
                        href={CONTACT.phoneHref}
                        data-testid="mobile-cta-call-tel"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-[hsl(30_35%_92%)] transition-colors active:bg-[hsl(var(--gold)/0.16)]"
                      >
                        <Phone size={15} className="text-[hsl(var(--gold-soft))]" /> Call us
                      </a>
                      <div className="h-px bg-[hsl(var(--gold)/0.18)]" />
                      <a
                        href={CONTACT.smsHref}
                        data-testid="mobile-cta-call-sms"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-[hsl(30_35%_92%)] transition-colors active:bg-[hsl(var(--gold)/0.16)]"
                      >
                        <MessageSquare size={15} className="text-[hsl(var(--gold-soft))]" /> Text us instead
                      </a>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <a
              href={QUOTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-cta-quote"
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-gold-gradient text-espresso text-xs uppercase tracking-[0.16em] font-semibold shadow-[0_4px_16px_hsl(38_58%_66%/0.35)] transition-transform duration-300 active:scale-[0.98]"
            >
              Instant Quote
              <ArrowUpRight size={14} />
            </a>
            <Link
              to="/consultation"
              data-testid="mobile-cta-consult"
              aria-label="Schedule a consultation"
              className="grid place-items-center h-11 w-11 shrink-0 rounded-full border border-[hsl(var(--gold)/0.45)] text-[hsl(var(--gold-soft))] transition-colors duration-300 active:bg-[hsl(var(--gold)/0.18)]"
            >
              <CalendarDays size={17} />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
